import { Link } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, AlertCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SEO, generateServiceSchema } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";

// Use thin lazy-wrapper modules (they lazy-load heavy internals internally)
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { TextReveal } from "@/components/ui/TextReveal";
import { ParallaxCard } from "@/components/ui/ParallaxCard";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
const RelatedLinks = lazy(() => import("./RelatedLinks").then((m) => ({ default: m.RelatedLinks })));
import type { MoneyPage, IndustryPage, InsightPage } from "@/data/contentClusters";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface MoneyPageTemplateProps {
  page: MoneyPage;
  pillarTitle: string;
  relatedMoneyPages: { title: string; slug: string }[];
  relatedIndustryPages: { title: string; slug: string }[];
  relatedInsightPages: { title: string; slug: string }[];
}

export function MoneyPageTemplate({
  page,
  pillarTitle,
  relatedMoneyPages,
  relatedIndustryPages,
  relatedInsightPages,
}: MoneyPageTemplateProps) {
  // Generate service schema for GEO/AEO
  const serviceSchema = generateServiceSchema({
    name: page.h1,
    description: page.metaDescription,
    url: `https://thecloudseo.com/services/${page.pillarSlug}/${page.slug}`,
    priceRange: page.pricing.starting,
  });

  // Extract FAQ items for schema
  const faqItems = page.faqs.map(faq => ({
    question: faq.question,
    answer: faq.answer,
  }));

  return (
    <Layout>
      <SEO
        title={`${page.title} | The Cloud SEO`}
        description={page.metaDescription}
        canonical={`https://thecloudseo.com/services/${page.pillarSlug}/${page.slug}`}
        type="service"
        jsonLd={serviceSchema}
        faqSchema={faqItems}
        keywords={[page.h1, pillarTitle, 'AI marketing', 'B2B', 'SaaS']}
        speakable={['h1', '.heading-section']}
      />
      <Suspense fallback={null}>
        <ScrollProgress />
      </Suspense>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <Suspense fallback={null}>
          <AnimatedBackground variant="mesh" />
        </Suspense>
        <Suspense fallback={null}>
          <FloatingShapes />
        </Suspense>

        <div className="container-wide relative z-10">
          <div className="max-w-4xl">
            {/* Breadcrumbs */}
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "Services", href: "/services" },
                { label: pillarTitle, href: `/services/${page.pillarSlug}` },
                { label: page.h1 },
              ]}
              className="mb-6"
            />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link 
                to={`/services/${page.pillarSlug}`}
                className="inline-flex items-center gap-2 text-primary mb-6 hover:underline"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to {pillarTitle}
              </Link>
            </motion.div>

            <Suspense fallback={<p className="text-lg text-primary mb-4">{page.subtitle}</p>}>
              <TextReveal>
                <p className="text-lg text-primary mb-4">{page.subtitle}</p>
              </TextReveal>
            </Suspense>
            <Suspense fallback={<h1 className="heading-hero mb-6">{page.h1}</h1>}>
              <TextReveal delay={0.1}>
                <h1 className="heading-hero mb-6">{page.h1}</h1>
              </TextReveal>
            </Suspense>
            <Suspense fallback={<p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mb-10">{page.description}</p>}>
              <TextReveal delay={0.2}>
                <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mb-10">
                  {page.description}
                </p>
              </TextReveal>
            </Suspense>

            <motion.div
              className="flex flex-wrap gap-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/contact">
                <Button
                  size="lg"
                  className="rounded-full px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {page.cta.buttonText} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="rounded-full px-8">
                From {page.pricing.starting}/mo
              </Button>
            </motion.div>

            {/* Hero Stats */}
            <div className="grid grid-cols-3 gap-6">
              {page.heroStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="text-center md:text-left"
                >
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <AnimatedBackground variant="grid" />
        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Problem */}
            <Suspense fallback={<div />}>
              <AnimatedSection>
                <div className="p-8 rounded-3xl bg-destructive/5 border border-destructive/20 h-full">
                <div className="flex items-center gap-2 mb-6">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                  <h3 className="text-sm font-semibold text-destructive uppercase tracking-wider">
                    The Problem
                  </h3>
                </div>
                <h4 className="text-2xl md:text-3xl font-bold mb-6">{page.problem.headline}</h4>
                <ul className="space-y-4">
                  {page.problem.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0" />
                      <span className="text-lg text-muted-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </Suspense>

            {/* Solution */}
            <Suspense fallback={<div />}>
              <AnimatedSection delay={0.2}>
                <div className="p-8 rounded-3xl bg-primary/5 border border-primary/20 h-full">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h3 className="text-sm font-semibold text-primary uppercase tracking-wider">
                    Our Solution
                  </h3>
                </div>
                <h4 className="text-2xl md:text-3xl font-bold mb-4">{page.solution.headline}</h4>
                <p className="text-lg text-muted-foreground mb-6">{page.solution.description}</p>
                <ul className="space-y-3">
                  {page.solution.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </Suspense>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-spacing relative">
        <AnimatedBackground variant="dots" />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center mb-16">
            <TextReveal>
              <h2 className="heading-section mb-4">What's Included</h2>
            </TextReveal>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to succeed
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {page.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <ParallaxCard
                  className="p-6 rounded-2xl bg-secondary/30 h-full"
                  intensity={3}
                >
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </ParallaxCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing bg-secondary/30 relative overflow-hidden">
        <FloatingShapes />
        <div className="container-wide relative z-10">
          <AnimatedSection className="text-center mb-16">
            <TextReveal>
              <h2 className="heading-section mb-4">Our Process</h2>
            </TextReveal>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {page.process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <div className="text-6xl font-bold text-primary/20 mb-4">{step.step}</div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section-spacing relative">
        <div className="container-narrow">
          <AnimatedSection>
            <div className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-center">
              <h2 className="heading-section mb-4">Simple, Transparent Pricing</h2>
              <div className="text-5xl md:text-6xl font-bold text-primary mb-2">
                {page.pricing.starting}
                <span className="text-2xl text-muted-foreground font-normal">/month</span>
              </div>
              <p className="text-lg text-muted-foreground mb-8">Starting from</p>

              <ul className="flex flex-wrap justify-center gap-4 mb-8">
                {page.pricing.includes.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact">
                <Button size="lg" className="rounded-full px-10">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-narrow">
          <AnimatedSection className="text-center mb-12">
            <TextReveal>
              <h2 className="heading-section mb-4">Frequently Asked Questions</h2>
            </TextReveal>
          </AnimatedSection>

          <Accordion type="single" collapsible className="space-y-4">
            {page.faqs.map((faq, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <AccordionItem
                  value={`faq-${index}`}
                  className="bg-background rounded-xl border border-border px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </AnimatedSection>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related Links */}
      <RelatedLinks
        pillarSlug={page.pillarSlug}
        pillarTitle={pillarTitle}
        moneyPages={relatedMoneyPages}
        industryPages={relatedIndustryPages}
        insightPages={relatedInsightPages}
        currentSlug={page.slug}
      />

      {/* CTA */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">{page.cta.headline}</h2>
            </TextReveal>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-10">
              {page.cta.description}
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                variant="secondary"
                className="rounded-full px-10 text-base h-14"
              >
                {page.cta.buttonText}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
